import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import projectIcon from "../../assets/icons/icon-project.png"
import companyIcon from "../../assets/icons/company-icon.png"
import { Input } from "../../components/Form"
import { ModalBody, ModalHeader, ModalPanel } from "../../components/Modal"
import { ListOrganizationsAndProjects } from "../../components/ListOrganizationsAndProjects"
import { setData, setDataMerge } from "../../core/redux/app/Actions"
import Send from "../../core/communication"
import { customToast } from "../../components/Toast"
import { isMobile } from "react-device-detect"
import SelectCompanyMobileContainer from "../SelectCompanyMobileContainer"
import {
  formatStringAndNormalizeToSearch,
  normalizeString,
  sortAlphabetically,
} from "../../core/utils/manipulateStrings"
import { removeDuplicatedArray, reduceArrayObjects } from "../../core/utils/manipulateArrays"
import { useForm } from "react-hook-form"
import { useOfflineFormProject } from "../../core/utils/hooks/useFormProject"

const replacedImage = (data, isCompany = false) => {
  const checkIsCompanyOrProject = isCompany ? companyIcon : projectIcon
  return (
    data?.length > 0 &&
    data
      ?.filter((item) => item)
      .map((obj) =>
        !obj.image || obj.image.includes("https://storage")
          ? { ...obj, image: checkIsCompanyOrProject }
          : obj,
      )
  )
}

const formatProjectsList = (data) => {
  return replacedImage(data).map(({ id, description, image }) => ({
    value: id,
    name: description,
    image,
  }))
}

export const useScope = () => {
  const scope = useSelector((state) => state?.app?.scope) || {}
  const scopeType = useSelector((state) => state?.app?.scopeType) || "project"
  const projectsByScope = useSelector((state) => state?.app?.projectsByScope) || []

  return {
    scope,
    scopeName: scope?.name || scope?.description,
    scopeType,
    projectsByScope: projectsByScope.length ? projectsByScope : [scope],
    projectConfig: scope?.config || {},
  }
}

export const useUser = () => {
  const user = useSelector((state) => state.app.user?.user || {})
  return user
}

export const useProjects = () => {
  const dispatch = useDispatch()
  const token = useSelector((state) => state.app?.token)
  const projects = useSelector((state) => state.app?.projects || [])
  const companies = useSelector((state) => state.app?.companiesWithoutOrg || [])
  const organizations = useSelector((state) => state.app?.organizations || [])
  const scope = useSelector((state) => {
    const s = state.app?.scope
    return s && Object.keys(s).length > 0 ? s : null // Se for um objeto vazio, retorna null
  })
  const scopeType = useSelector((state) => state.app?.scopeType || "project")
  const [loadingProjects, setLoadingProjects] = useState(false)

  const changeScope = (scopeId, scopeType = "project") => {
    let newScope = null
    let newProjects = []

    if (scopeType === "project") {
      newScope = projects.find((item) => item?.id === scopeId) || null
      newProjects = newScope ? [newScope] : []
    } else if (scopeType === "company") {
      newScope = companies.find((company) => company?.id === scopeId) || null
      newProjects = newScope?.projects ? [...newScope.projects] : []
    } else if (scopeType === "organization") {
      newScope = organizations.find((organization) => organization?.id === scopeId) || null
      newProjects = []

      if (newScope?.companies) {
        newScope.companies.forEach((company) => {
          if (Array.isArray(company.projects)) {
            newProjects = [...newProjects, ...company.projects]
          }
        })
      }
    }

    dispatch(
      setData({
        scope: newScope,
        scopeType,
        projectsByScope: newProjects,
        projectConfig: newScope?.config?.hasEsm ?? null,
        farmerFilters: {},
        shouldRedirectToUsers: false,
      }),
    )
  }

  const loadCompany = async () => {
    setLoadingProjects(true)
    let allProjects = []

    try {
      const response = await Send.get("/organizations/companies-projects")

      if (!response?.organizations || !response?.companies || !response?.projects) {
        customToast("Erro ao atualizar projetos", "error")
        setLoadingProjects(false)
        return
      }

      response.organizations.forEach((organization) => {
        organization.companies.forEach((company) => {
          if (Array.isArray(company.projects)) {
            allProjects = [...allProjects, ...company.projects]
          }
        })
      })

      response.companies.forEach((company) => {
        if (Array.isArray(company.projects)) {
          allProjects = [...allProjects, ...company.projects]
        }
      })

      const withoutCategory = {
        id: 0,
        name: "Outras",
        companies: [
          {
            id: 0,
            name: "Todos os Projetos",
            projects: sortAlphabetically(response.projects, "description"),
          },
          ...sortAlphabetically(response.companies),
        ],
      }

      const newProjects =
        sortAlphabetically(
          removeDuplicatedArray([...allProjects, ...response.projects], "id"),
          "description",
        ) || []

      dispatch(
        setData({
          organizations: [...sortAlphabetically(response.organizations), withoutCategory] ?? [],
          companiesWithoutOrg: sortAlphabetically(response.companies) ?? [],
          projects: newProjects,
        }),
      )

      setLoadingProjects(false)
    } catch (error) {
      console.error("Erro ao buscar projetos:", error)
      setLoadingProjects(false)
    }
  }

  // Carrega os projetos ao montar o hook
  useEffect(() => {
    if(token){
      loadCompany()
    }
  }, [token])

  // Define o escopo inicial assim que os projetos forem carregados
  useEffect(() => {
    if (scope === null && projects.length > 0) {
      changeScope(projects[0]?.id, "project")
    }
  }, [projects]) // Dispara apenas quando `projects` mudar

  return {
    projects,
    scope,
    scopeType,
    changeScope,
    loadingProjects,
  }
}

export const SelectCompaniesContainer = ({
  data,
  openCompanyModal,
  setOpenCompanyModal,
  setOpenCompanyMenu,
  openCompanyMenu,
  collapsed,
}) => {
  const { scope, scopeName, scopeType } = useScope()
  const [selectedCompany, setSelectedCompany] = useState({})
  const [filtered, setFilter] = useState([])
  const [projects, setProjects] = useState([])
  const [selectedProject, setSelectedProject] = useState({})
  const [dataModal, setDataModal] = useState([])
  const { changeScope } = useProjects()
  const [filteringCompany, setFilteringCompany] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [isQrCodeModalOpen, setIsQrCodeModalOpen] = useState(false)
  const [isAddNumberOpen, setIsAddNumberOpen] = useState(false)
  const _preloadForm = useOfflineFormProject(scope?.id, scopeType)

  useEffect(() => {
    if ((data && data.length) || !openCompanyModal) {
      setSearchTerm("")
      setFilter(data)
    }
  }, [data, openCompanyModal])

  const handleClick = (company) => {
    setSelectedCompany(company)
  }

  const handleClickProject = ({ value }) => {
    console.log("handleclickProject")
    changeScope(value)
    handleCloseModals()
  }

  const handleSelectCompany = (type, value) => {
    changeScope(value, type)
    // handleCloseModals()
  }

  const handleCloseModals = () => {
    setOpenCompanyModal(false)
    setOpenCompanyMenu(false)
  }

  const handleFilterByCompanyOrProject = ({ search }) => {
    setSearchTerm(search)

    const dataToFilter = [...data]

    const filteredCompanies = dataToFilter.filter((company) =>
      formatStringAndNormalizeToSearch(company?.name, search),
    )

    const filteredProjects = dataToFilter.filter((item) =>
      item.projects.some((project) =>
        formatStringAndNormalizeToSearch(project?.description, search),
      ),
    )

    let newFiltered = []

    if (dataToFilter[0]?.name === "Todos os Projetos") {
      const getAllProjects = filteredProjects.map((item) => {
        const filteredProjects = item.projects.filter((project) =>
          normalizeString(project.description).includes(normalizeString(search)),
        )
        return { ...item, projects: filteredProjects }
      })

      newFiltered = getAllProjects
    } else {
      newFiltered = filteredProjects
    }

    setFilteringCompany(filteredCompanies.length > 0)
    setFilter([...newFiltered, ...filteredCompanies])
  }

  useEffect(() => {
    const selectedCompanyId = filtered?.length >= 1 ? filtered[0]?.id : 0
    setSelectedCompany({ value: selectedCompanyId })
    setProjects(reduceArrayObjects(filtered))
  }, [filtered])

  useEffect(() => {
    let data = []
    if (selectedCompany?.projects?.length > 0) {
      data = formatProjectsList(selectedCompany?.projects)
    }
    setDataModal(data)
  }, [selectedCompany, searchTerm])

  useEffect(() => {
    let data = []
    if (projects?.length > 0) {
      const dataProject = removeDuplicatedArray(formatProjectsList(projects), "value")

      if (searchTerm !== "") {
        data = dataProject.sort(
          (a, b) =>
            normalizeString(b.name).includes(normalizeString(searchTerm)) -
            normalizeString(a.name).includes(normalizeString(searchTerm)),
        )
      } else {
        data = dataProject
      }
    }
    setDataModal(data)
  }, [projects, searchTerm])

  // companyReplacedImage
  const companyReplacedImage = replacedImage(removeDuplicatedArray(filtered, "id"), true)

  const handleCloseQrModal = () => {
    console.log("AQUI")
    setIsQrCodeModalOpen(false)
  }

  const handleOpenQrModal = (project) => {
    setIsQrCodeModalOpen(true)
    setSelectedProject(project || {})
  }

  const handleCloseAddModal = () => {
    setIsAddNumberOpen(false)
  }

  const handleOpenAddModal = (project) => {
    setIsAddNumberOpen(true)
    setSelectedProject(project || {})
  }

  return (
    <div className="project-container">
      <ModalPanel modalIsOpen={openCompanyModal}>
        {isMobile ? (
          <div className="form-header">
            <div
              style={{ display: "flex", gap: "10px" }}
              onClick={() => setOpenCompanyModal(false)}>
              <button className="button-back">
                <i className="fi fi-sr-arrow-small-left" />
              </button>
              <div className="title">{`${scopeName}`}</div>
            </div>
          </div>
        ) : (
          <ModalHeader
            closeModal={() => setOpenCompanyModal(false)}
            title="Selecione uma empresa ou projeto"
          />
        )}

        {isMobile ? (
          <></>
        ) : (
          <div style={{ padding: "16px 16 0 16" }}>
            <Input
              placeholder="Pesquisar pelo nome da empresa ou projeto"
              onChange={(e) => handleFilterByCompanyOrProject({ search: e.target.value })}
              hasIcon={<i class="fi fi-rr-search"></i>}
              hasFocus={false}
            />
          </div>
        )}
        {isMobile ? (
          <></>
        ) : (
          <div className="display-filtering">
            {searchTerm &&
              `${
                filteringCompany
                  ? "Você está filtrando por empresas"
                  : "Você está filtrando por projetos"
              }`}
          </div>
        )}
        <ModalBody className="modal-project-container">
          {isMobile ? (
            <></>
          ) : (
            <ListOrganizationsAndProjects
              data={
                (companyReplacedImage &&
                  companyReplacedImage.map(({ id, name, image, projects }) => ({
                    value: id,
                    name,
                    image,
                    projects,
                  }))) ||
                []
              }
              selectedCompany={selectedCompany}
              handleClick={handleClick}
              handleSelect={(value) => handleSelectCompany("company", value)}
              currentScope={scope?.id}
              handleCloseQrModal={handleCloseQrModal}
              handleOpenQrModal={handleOpenQrModal}
              isQrCodeModalOpen={isQrCodeModalOpen}
              handleCloseAddModal={handleCloseAddModal}
              handleOpenAddModal={handleOpenAddModal}
              isAddNumberOpen={isAddNumberOpen}
            />
          )}

          {isMobile ? (
            <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
              <SelectCompanyMobileContainer
                {...{ openCompanyMenu, setOpenCompanyMenu, collapsed }}
              />
              <ListOrganizationsAndProjects
                data={dataModal}
                handleClick={handleClickProject}
                projects
                handleCloseQrModal={handleCloseQrModal}
                handleOpenQrModal={handleOpenQrModal}
                isQrCodeModalOpen={isQrCodeModalOpen}
                selectedProject={selectedProject}
                onChange={(e) => handleFilterByCompanyOrProject({ search: e.target.value })}
                handleCloseAddModal={handleCloseAddModal}
                handleOpenAddModal={handleOpenAddModal}
                isAddNumberOpen={isAddNumberOpen}
              />
            </div>
          ) : (
            <ListOrganizationsAndProjects
              data={dataModal}
              handleClick={handleClickProject}
              projects
              handleCloseQrModal={handleCloseQrModal}
              handleOpenQrModal={handleOpenQrModal}
              isQrCodeModalOpen={isQrCodeModalOpen}
              selectedProject={selectedProject}
              handleCloseAddModal={handleCloseAddModal}
              handleOpenAddModal={handleOpenAddModal}
              isAddNumberOpen={isAddNumberOpen}
            />
          )}
        </ModalBody>
      </ModalPanel>
    </div>
  )
}
